import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    fetchBoardRequest,
    updateBoardRequest,
    resetBoardState
} from '../../store/boards/actions/board-actions';
import {
    createTaskRequest,
    updateTaskRequest,
    deleteTaskRequest,
    resetTaskState
} from '../../store/boards/actions/task-actions';
import TaskCard from '../../components/boards/TaskCard';
import TaskModal from '../../components/boards/TaskModal';

const TaskStatus = {
    OPEN: 'OPEN',
    IN_PROGRESS: 'IN_PROGRESS',
    DONE: 'DONE',
    BLOCKED: 'BLOCKED'
};

const TaskPriority = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
};

const BoardDetailsPage = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { currentBoard, loading, updateSuccess } = useSelector((state) => state.boards || {});
    const { createSuccess, updateSuccess: taskUpdateSuccess } = useSelector((state) => state.tasks || {});
    const users = useSelector(state => state.user?.users || []);

    const [showTaskModal, setShowTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [draggedTask, setDraggedTask] = useState(null);
    const [isEditingBoard, setIsEditingBoard] = useState(false);
    const [boardFormData, setBoardFormData] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        dispatch(fetchBoardRequest(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentBoard) {
            setBoardFormData({
                name: currentBoard.name || '',
                description: currentBoard.description || ''
            });
        }
    }, [currentBoard]);

    useEffect(() => {
        if (createSuccess || taskUpdateSuccess) {
            setShowTaskModal(false);
            setEditingTask(null);
            dispatch(resetTaskState());
            dispatch(fetchBoardRequest(id));
        }
    }, [createSuccess, taskUpdateSuccess, dispatch, id]);

    useEffect(() => {
        if (updateSuccess) {
            setIsEditingBoard(false);
            dispatch(resetBoardState());
            dispatch(fetchBoardRequest(id));
        }
    }, [updateSuccess, dispatch, id]);

    const getUserFullName = (userId, users) => {
        if (!userId || !users) return 'N/A';
        const user = users.find(u => u.id === userId);
        if (user) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || 'N/A';
        }
        return 'N/A';
    };

    const handleCreateTask = useCallback((status) => {
        setSelectedStatus(status);
        setEditingTask(null);
        setShowTaskModal(true);
    }, []);

    const handleEditTask = useCallback((task) => {
        setEditingTask(task);
        setShowTaskModal(true);
    }, []);

    const handleDeleteTask = useCallback((taskId) => {
        if (window.confirm(t('task.confirmDeleteTask'))) {
            dispatch(deleteTaskRequest(taskId));
            setTimeout(() => {
                dispatch(fetchBoardRequest(id));
            }, 500);
        }
    }, [dispatch, id, t]);

    const handleTaskSubmit = useCallback((taskData) => {
        if (editingTask) {
            dispatch(updateTaskRequest(editingTask.id, taskData));
        } else {
            const payload = {
                ...taskData,
                status: selectedStatus || TaskStatus.OPEN
            };
            dispatch(createTaskRequest(id, payload));
        }
    }, [editingTask, selectedStatus, dispatch, id]);

    const handleDragStart = useCallback((e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback((e, newStatus) => {
        e.preventDefault();
        if (draggedTask && draggedTask.status !== newStatus) {
            dispatch(updateTaskRequest(draggedTask.id, { status: newStatus }));
            setTimeout(() => {
                dispatch(fetchBoardRequest(id));
            }, 500);
        }
        setDraggedTask(null);
    }, [draggedTask, dispatch, id]);

    const handleUpdateBoard = useCallback(() => {
        if (!boardFormData.name.trim()) {
            alert(t('board.boardName') + ' ' + t('common.required'));
            return;
        }
        dispatch(updateBoardRequest(id, boardFormData));
    }, [dispatch, id, boardFormData, t]);

    const handleCancelEditBoard = useCallback(() => {
        setIsEditingBoard(false);
        setBoardFormData({
            name: currentBoard?.name || '',
            description: currentBoard?.description || ''
        });
    }, [currentBoard]);

    const getTasksByStatus = (status) => {
        return currentBoard?.tasks?.filter(task => task.status === status) || [];
    };

    const getStatusColor = (status) => {
        switch (status) {
            case TaskStatus.OPEN:
                return 'bg-blue-500';
            case TaskStatus.IN_PROGRESS:
                return 'bg-yellow-500';
            case TaskStatus.DONE:
                return 'bg-green-500';
            case TaskStatus.BLOCKED:
                return 'bg-red-500';
            default:
                return 'bg-gray-500';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case TaskStatus.OPEN:
                return t('task.status.open');
            case TaskStatus.IN_PROGRESS:
                return t('task.status.inProgress');
            case TaskStatus.DONE:
                return t('task.status.done');
            case TaskStatus.BLOCKED:
                return t('task.status.blocked');
            default:
                return status;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'dd.MM.yyyy HH:mm');
        } catch (ex) {
            console.error(ex);
            return "";
        }
    };

    if (loading && !currentBoard) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex flex-col items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#34495e] mb-4"></div>
                    <p className="text-gray-500">{t('board.loadingBoards')}</p>
                </div>
            </div>
        );
    }

    if (!currentBoard) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-white rounded-lg shadow-md p-6 text-center">
                    <p className="text-gray-500">{t('board.boardNotFound')}</p>
                    <button
                        onClick={() => navigate('/boards')}
                        className="mt-4 bg-[#34495e] hover:bg-[#2c3e50] text-white font-medium py-2 px-6 rounded-md"
                    >
                        {t('board.backToBoards')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                        {isEditingBoard ? (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('board.boardName')} *
                                    </label>
                                    <input
                                        type="text"
                                        value={boardFormData.name}
                                        onChange={(e) => setBoardFormData({ ...boardFormData, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                        placeholder={t('board.enterBoardName')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('common.description')}
                                    </label>
                                    <textarea
                                        value={boardFormData.description}
                                        onChange={(e) => setBoardFormData({ ...boardFormData, description: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                                        placeholder={t('board.enterDescription')}
                                        rows="3"
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleUpdateBoard}
                                        disabled={loading}
                                        className="bg-[#34495e] hover:bg-[#2c3e50] disabled:bg-gray-400 text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                                    >
                                        {t('common.save')}
                                    </button>
                                    <button
                                        onClick={handleCancelEditBoard}
                                        disabled={loading}
                                        className="bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-800 font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                                    >
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-4">
                                    <h1 className="text-3xl font-bold text-[#34495e]">{currentBoard.name}</h1>
                                    <button
                                        onClick={() => setIsEditingBoard(true)}
                                        className="text-gray-500 hover:text-[#34495e]"
                                        title={t('board.editBoard')}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                        </svg>
                                    </button>
                                </div>
                                {currentBoard.description && (
                                    <p className="text-gray-600 mt-2">{currentBoard.description}</p>
                                )}
                                <div className="flex gap-6 mt-4 text-sm text-gray-500">
                                    <span>{t('common.createdAt')}: {formatDate(currentBoard.createdAt)}</span>
                                    <span>{t('common.createdBy')}: {getUserFullName(currentBoard.createdBy, users)}</span>
                                    <span>{t('board.totalTasks')}: {currentBoard.totalTasks || 0}</span>
                                </div>
                            </>
                        )}
                    </div>
                    {!isEditingBoard && (
                        <button
                            onClick={() => navigate('/boards')}
                            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                        >
                            {t('board.backToBoards')}
                        </button>
                    )}
                </div>
            </div>

            {/* Kanban Board */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[TaskStatus.OPEN, TaskStatus.IN_PROGRESS, TaskStatus.BLOCKED, TaskStatus.DONE].map((status) => (
                    <div
                        key={status}
                        className="bg-gray-50 rounded-lg p-4"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, status)}
                    >
                        {/* Column Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`}></div>
                                <h2 className="text-lg font-semibold text-gray-800">
                                    {getStatusLabel(status)}
                                </h2>
                                <span className="bg-gray-200 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full">
                                    {getTasksByStatus(status).length}
                                </span>
                            </div>
                            <button
                                onClick={() => handleCreateTask(status)}
                                className="text-gray-500 hover:text-[#34495e]"
                                title={t('task.addTask')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        {/* Tasks */}
                        <div className="space-y-3 min-h-[200px]">
                            {getTasksByStatus(status).map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    users={users}
                                    onEdit={handleEditTask}
                                    onDelete={handleDeleteTask}
                                    onDragStart={handleDragStart}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Task Modal */}
            {showTaskModal && (
                <TaskModal
                    task={editingTask}
                    users={users}
                    onClose={() => {
                        setShowTaskModal(false);
                        setEditingTask(null);
                        setSelectedStatus(null);
                    }}
                    onSubmit={handleTaskSubmit}
                />
            )}
        </div>
    );
};

export default BoardDetailsPage;
